"use client";
import React, { useState, useEffect, useRef } from "react";

const BackToTopButton = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [scrollPct, setScrollPct] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const onScroll = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const scrolled = window.scrollY;
        const total = document.documentElement.scrollHeight - window.innerHeight;
        setScrollPct(total > 0 ? Math.min((scrolled / total) * 100, 100) : 0);
        setIsVisible(scrolled > 320);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const scrollToTop = () => {
    setIsPressed(true);
    setTimeout(() => setIsPressed(false), 200);

    // Custom eased scroll animation (easeInOutQuad) instead of native smooth scroll
    const start = window.scrollY;
    const duration = 600;
    let startTime: number | null = null;

    const easeInOutQuad = (t: number) =>
      t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      window.scrollTo(0, start * (1 - easeInOutQuad(progress)));
      if (progress < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  };

  // Progress circle calculations
  const size = 56;
  const strokeWidth = 2.5;
  const radius = 24;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (scrollPct / 100) * circumference;

  return (
    <button
      onClick={scrollToTop}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      aria-label="Back to top"
      className="fixed bottom-28 right-8 z-50 focus:outline-none group"
      style={{
        width: `${size}px`,
        height: `${size}px`,
        opacity: isVisible ? 1 : 0,
        transform: isVisible
          ? isPressed
            ? "translateY(0) scale(0.95)"
            : isHovered
            ? "translateY(-2px) scale(1.05)"
            : "translateY(0) scale(1)"
          : "translateY(16px) scale(0.9)",
        pointerEvents: isVisible ? "auto" : "none",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {/* Outer glow effect */}
      <div
        className="absolute inset-0 rounded-full pointer-events-none"
        style={{
          background: isHovered
            ? "radial-gradient(circle, rgba(255, 95, 31, 0.15) 0%, transparent 70%)"
            : "transparent",
          transform: "scale(1.8)",
          transition: "background 0.3s ease",
        }}
      />

      {/* Progress ring SVG */}
      <svg
        width={size}
        height={size}
        viewBox="0 0 56 56"
        className="absolute inset-0"
        style={{ transform: "rotate(-90deg)" }}
      >
        {/* Background track */}
        <circle
          cx="28"
          cy="28"
          r={radius}
          fill="none"
          stroke="rgba(255, 95, 31, 0.08)"
          strokeWidth="1.5"
        />
        {/* Progress circle */}
        <circle
          cx="28"
          cy="28"
          r={radius}
          fill="none"
          stroke="#FF5F1F"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          style={{
            transition: "stroke-dashoffset 0.2s ease",
            filter: isHovered
              ? "drop-shadow(0 0 6px rgba(255, 95, 31, 0.4))"
              : "none",
          }}
        />
      </svg>

      {/* Main button */}
      <div
        className="absolute inset-1 rounded-full flex items-center justify-center transition-all duration-300"
        style={{
          background: isHovered ? "#222222" : "#1A1A1A",
          boxShadow: isHovered
            ? "0 8px 25px rgba(255, 95, 31, 0.25), 0 3px 10px rgba(0, 0, 0, 0.4)"
            : "0 10px 30px rgba(0, 0, 0, 0.6)",
          border: `1px solid ${isHovered ? "rgba(255, 95, 31, 0.3)" : "rgba(255, 255, 255, 0.1)"}`,
        }}
      >
        {/* Arrow icon — dual icon slides up on hover */}
        <div className="relative overflow-hidden h-[20px] w-[20px]">
          <div
            className="flex flex-col"
            style={{
              transform: isHovered ? "translateY(-21px)" : "translateY(0)",
              transition: "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <svg
              width="20"
              height="20"
              fill="none"
              viewBox="0 0 24 24"
              stroke={isHovered ? "#FF5F1F" : "#ffffff"}
              strokeWidth={3}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 19V5M5 12l7-7 7 7"
              />
            </svg>
            <svg
              width="20"
              height="20"
              fill="none"
              viewBox="0 0 24 24"
              stroke="#FF5F1F"
              strokeWidth={3}
              style={{ marginTop: "1px" }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 19V5M5 12l7-7 7 7"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Tooltip */}
      <div
        className="absolute right-full mr-4 top-1/2 -translate-y-1/2 whitespace-nowrap pointer-events-none"
        style={{
          opacity: isHovered ? 1 : 0,
          transform: isHovered
            ? "translateY(-50%) translateX(0)"
            : "translateY(-50%) translateX(8px)",
          transition: "all 0.3s ease",
        }}
      >
        <div className="bg-[#1A1A1A] border border-white/10 text-white text-xs font-medium px-3 py-2 rounded-lg shadow-lg relative">
          Back to top
          {/* Arrow pointing to button */}
          <div className="absolute left-full top-1/2 -translate-y-1/2 w-0 h-0 border-l-4 border-l-[#1A1A1A] border-t-4 border-t-transparent border-b-4 border-b-transparent" />
        </div>
      </div>

      {/* Ripple effect on click */}
      {isPressed && (
        <div
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(255, 95, 31, 0.3) 0%, transparent 70%)",
            animation: "ripple 0.6s ease-out",
          }}
        />
      )}

      <style jsx>{`
        @keyframes ripple {
          0% {
            transform: scale(0);
            opacity: 1;
          }
          100% {
            transform: scale(2);
            opacity: 0;
          }
        }
      `}</style>
    </button>
  );
};

export default BackToTopButton;
